import { Image } from "@phosphor-icons/react"
import { CloudUpload } from "lucide-react"
import { useRef } from "react"

import { Button } from "@/components/ui/button"
import { Label } from "@/components/ui/label"
interface ImageUploadProps {
  label: string
  id: string
  preview?: string | null
  currentUrl?: string | null
  onFileChange: (e: React.ChangeEvent<HTMLInputElement>) => void
  onRemove?: () => void
  error?: string
  buttonLabel?: string
  variant?: "avatar" | "logo"
}

export default function ImageUpload({
  label,
  id,
  preview,
  currentUrl,
  onFileChange,
  onRemove,
  error,
  buttonLabel = "Upload image",
  variant = "avatar",
}: ImageUploadProps) {
  const fileInputRef = useRef<HTMLInputElement>(null)
  const imageSrc = preview ?? currentUrl ?? null
  const hasImage = Boolean(imageSrc)

  function handleRemove() {
    if (fileInputRef.current) fileInputRef.current.value = ""
    onRemove?.()
  }

  return (
    <div className="grid gap-2">
      <Label htmlFor={id}>{label}</Label>
      <div className="flex items-center gap-4">
        <button
          type="button"
          onClick={() => fileInputRef.current?.click()}
          className="group relative flex shrink-0 cursor-pointer items-center justify-center rounded-lg border-2 border-dashed border-zinc-200 transition-colors hover:border-zinc-300 dark:border-zinc-700 dark:hover:border-zinc-600"
          style={{ width: 96, height: 96 }}
        >
          <div className="size-16 overflow-hidden rounded-full">
            {imageSrc ? (
              <img
                src={imageSrc}
                alt={label}
                className="h-full w-full object-cover"
              />
            ) : variant === "avatar" ? (
              <svg
                viewBox="0 0 80 80"
                fill="none"
                className="h-full w-full"
                aria-hidden="true"
              >
                <circle
                  cx="40"
                  cy="40"
                  r="40"
                  className="fill-zinc-200 dark:fill-zinc-800"
                />
                <circle
                  cx="40"
                  cy="32"
                  r="14"
                  className="fill-zinc-100 dark:fill-zinc-600"
                />
                <ellipse
                  cx="40"
                  cy="68"
                  rx="22"
                  ry="14"
                  className="fill-zinc-100 dark:fill-zinc-600"
                />
              </svg>
            ) : (
              <div className="relative flex h-full w-full items-end justify-center overflow-hidden bg-zinc-200 dark:bg-zinc-800">
                <Image
                  weight="fill"
                  className="size-14 translate-y-3 text-zinc-100 dark:text-zinc-600"
                />
              </div>
            )}
          </div>
        </button>

        <div className="flex flex-col gap-2">
          <div className="flex items-center gap-2">
            <Button
              type="button"
              variant="outline"
              className="gap-2"
              onClick={() => fileInputRef.current?.click()}
            >
              <CloudUpload className="size-4" />
              {buttonLabel}
            </Button>
            {onRemove && hasImage && (
              <Button
                type="button"
                variant="ghost"
                className="text-muted-foreground hover:text-destructive"
                onClick={handleRemove}
              >
                Remove
              </Button>
            )}
          </div>
          <p className="text-muted-foreground text-xs">JPG or PNG. 10MB max.</p>
          {error && <p className="text-destructive text-sm">{error}</p>}
        </div>

        <input
          id={id}
          ref={fileInputRef}
          type="file"
          accept="image/png,image/jpeg"
          className="hidden"
          onChange={onFileChange}
        />
      </div>
    </div>
  )
}
